import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle2, Loader2, Download, Mail, Phone,
  UserPlus, Coins, Wrench, Flag, Trophy, Users, Sparkles,
  GraduationCap, HeartHandshake,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POSTER_URL = "/partnerships/arvo-farm-it-forward.pdf";
const SUPPORT_EMAIL = "support@arvo.co.nz";
const SUPPORT_PHONE = "021 327 944";
const SUPPORT_PHONE_TEL = "+64211327944";

const ORG_TYPES = [
  { value: "golf_club", label: "Golf Club" },
  { value: "sports_club", label: "Sports Club" },
  { value: "school", label: "Rural School" },
  { value: "community_group", label: "Community Group" },
  { value: "other", label: "Other" },
];

function Stat({ value, label, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-[#D5D1C8] rounded-md p-6 shadow-sm">
      <div
        className={`inline-flex items-center justify-center w-10 h-10 rounded-full mb-4 ${accent || "bg-[#2D4A22]/10 text-[#2D4A22]"}`}
      >
        <Icon className="w-5 h-5" strokeWidth={2.2} />
      </div>
      <p className="font-display font-bold text-4xl text-[#1A1D1A]">{value}</p>
      <p className="mt-1 text-sm font-bold text-[#1A1D1A]">{label}</p>
      {sub && <p className="mt-2 text-sm text-[#4A4F4A] leading-relaxed">{sub}</p>}
    </div>
  );
}

function Step({ n, title, body, icon: Icon }) {
  return (
    <div className="relative pl-16">
      <span
        aria-hidden
        className="absolute left-0 top-0 inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#2D4A22] text-white font-display font-bold text-lg"
      >
        {n}
      </span>
      <div className="flex items-center gap-2 text-[#D96C32] mb-2">
        <Icon className="w-4 h-4" />
        <h3 className="font-display font-bold text-lg text-[#1A1D1A]">{title}</h3>
      </div>
      <p className="text-[#4A4F4A] leading-relaxed">{body}</p>
    </div>
  );
}

function RegisterForm() {
  const [form, setForm] = useState({
    club_name: "",
    contact_name: "",
    email: "",
    phone: "",
    region: "",
    member_count: "",
    notes: "",
    organisation_type: "golf_club",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.club_name.trim() || !form.contact_name.trim() || !form.email.trim()) {
      toast.error("Club name, contact name and email are required.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        member_count: form.member_count ? parseInt(form.member_count, 10) : null,
      };
      const { data } = await axios.post(`${API}/golf-partnerships`, payload);
      setDone(true);
      toast.success(data.message);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong — please email us instead.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="golf-register-success"
        className="bg-white border border-[#2D4A22]/30 rounded-md p-8 text-center shadow-sm"
      >
        <CheckCircle2 className="w-12 h-12 text-[#2D4A22] mx-auto" />
        <h3 className="mt-4 font-display font-bold text-2xl text-[#1A1D1A]">
          You&apos;re on the list.
        </h3>
        <p className="mt-3 text-[#4A4F4A] leading-relaxed max-w-md mx-auto">
          One of the Arvo team will be in touch within a couple of business
          days to line up your Farm It Forward proposal and a custom, branded
          poster for your community.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <a
            href={POSTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="golf-download-poster-success"
            className="inline-flex items-center gap-2 bg-[#2D4A22] hover:bg-[#1F3A18] text-white font-bold px-5 py-2.5 rounded-md transition-colors"
          >
            <Download className="w-4 h-4" /> Download poster (PDF)
          </a>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="inline-flex items-center gap-2 border border-[#D5D1C8] text-[#1A1D1A] hover:bg-[#F9F8F6] font-bold px-5 py-2.5 rounded-md transition-colors"
          >
            <Mail className="w-4 h-4" /> Email the team
          </a>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="golf-register-form"
      className="bg-white border border-[#D5D1C8] rounded-md p-7 shadow-sm space-y-4"
    >
      <div className="flex items-center gap-2 text-[#D96C32] mb-2">
        <HeartHandshake className="w-5 h-5" strokeWidth={2.2} />
        <span className="text-xs font-bold tracking-[0.18em] uppercase">
          Register your community
        </span>
      </div>
      <h3 className="font-display font-bold text-2xl text-[#1A1D1A]">
        Ask us to call your committee.
      </h3>
      <p className="text-sm text-[#4A4F4A]">
        Drop your details in and we&apos;ll ring the number below within a
        couple of business days. Whether you&apos;re a golf club, sports club,
        rural school or community group, we&apos;ll walk you through the
        numbers, the onboarding and a tailored proposal.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
        <div className="sm:col-span-2">
          <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
            What kind of community group? *
          </label>
          <div className="flex flex-wrap gap-2">
            {ORG_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                data-testid={`golf-org-${t.value}`}
                onClick={() => setForm((s) => ({ ...s, organisation_type: t.value }))}
                className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                  form.organisation_type === t.value
                    ? "bg-[#2D4A22] border-[#2D4A22] text-white"
                    : "bg-white border-[#D5D1C8] text-[#1A1D1A] hover:border-[#2D4A22]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
            Club / school name *
          </label>
          <input
            type="text"
            data-testid="golf-club-name"
            required
            value={form.club_name}
            onChange={set("club_name")}
            placeholder="e.g. Waipara Golf Club"
            className="w-full border border-[#D5D1C8] rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2D4A22]"
          />
        </div>
        <div>
          <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
            Contact name *
          </label>
          <input
            type="text"
            data-testid="golf-contact-name"
            required
            value={form.contact_name}
            onChange={set("contact_name")}
            placeholder="e.g. Sam Morrison"
            className="w-full border border-[#D5D1C8] rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2D4A22]"
          />
        </div>
        <div>
          <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
            Email *
          </label>
          <input
            type="email"
            data-testid="golf-email"
            required
            value={form.email}
            onChange={set("email")}
            placeholder="you@club.co.nz"
            className="w-full border border-[#D5D1C8] rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2D4A22]"
          />
        </div>
        <div>
          <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
            Phone
          </label>
          <input
            type="tel"
            data-testid="golf-phone"
            value={form.phone}
            onChange={set("phone")}
            placeholder="021 123 4567"
            className="w-full border border-[#D5D1C8] rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2D4A22]"
          />
        </div>
        <div>
          <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
            Region
          </label>
          <input
            type="text"
            data-testid="golf-region"
            value={form.region}
            onChange={set("region")}
            placeholder="e.g. North Canterbury"
            className="w-full border border-[#D5D1C8] rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2D4A22]"
          />
        </div>
        <div>
          <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
            Approx. member count
          </label>
          <input
            type="number"
            min="0"
            data-testid="golf-member-count"
            value={form.member_count}
            onChange={set("member_count")}
            placeholder="e.g. 180"
            className="w-full border border-[#D5D1C8] rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2D4A22]"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
          Anything we should know?
        </label>
        <textarea
          data-testid="golf-notes"
          value={form.notes}
          onChange={set("notes")}
          rows={3}
          placeholder="Event coming up, member profile, notice-board plans, etc."
          className="w-full border border-[#D5D1C8] rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2D4A22]"
        />
      </div>

      <button
        type="submit"
        data-testid="golf-register-submit"
        disabled={loading}
        className="w-full bg-[#D96C32] hover:bg-[#B85A28] text-white font-bold py-4 rounded-md shadow-sm hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:hover:translate-y-0"
      >
        {loading ? (
          <span className="inline-flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Sending…
          </span>
        ) : (
          <span className="inline-flex items-center justify-center gap-2">
            Request a callback <ArrowRight className="w-5 h-5" />
          </span>
        )}
      </button>
    </form>
  );
}

export default function Golf() {
  return (
    <div className="min-h-screen bg-[#F2EFE7]">
      {/* Simple top bar back to main site */}
      <header className="bg-white border-b border-[#D5D1C8]">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-8 py-4">
          <a href="/" className="inline-flex items-center gap-3" data-testid="golf-back-home">
            <span
              role="img"
              aria-label="Arvo"
              className="block h-9 w-9 rounded-full bg-[#F9F8F6] bg-no-repeat bg-center bg-contain"
              style={{ backgroundImage: "url('/screens/logo-cow.png')" }}
            />
            <span className="flex flex-col leading-tight">
              <span className="font-display font-bold text-lg text-[#1A1D1A]">arvo</span>
              <span className="text-[10px] font-bold tracking-[0.14em] uppercase text-[#4A4F4A]">
                by Measured Livestock
              </span>
            </span>
          </a>
          <a
            href={POSTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="golf-poster-link-nav"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-[#2D4A22] hover:text-[#D96C32] transition-colors"
          >
            <Download className="w-4 h-4" /> Download poster
          </a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/1325658/pexels-photo-1325658.jpeg')",
          }}
          aria-hidden
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(15,31,12,.94) 0%, rgba(15,31,12,.86) 40%, rgba(15,31,12,.5) 100%)",
          }}
          aria-hidden
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-20 lg:py-24 text-white">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-3.5 py-1.5">
            <HeartHandshake className="w-4 h-4 text-[#F2C76A]" strokeWidth={2.5} />
            <span className="text-xs font-bold tracking-[0.18em] uppercase text-[#F2C76A]">
              Farm It Forward
            </span>
          </div>

          <h1
            data-testid="golf-hero-headline"
            className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-3xl"
          >
            Farm cattle.{" "}
            <span className="text-[#F2C76A]">Farm it forward.</span>
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-white/90 leading-relaxed max-w-2xl">
            Local clubs, schools and community groups keep rural New Zealand
            ticking. Farm It Forward turns the everyday cattle work your members
            and families already do into a steady funding stream — no bake
            sales, no raffle books, no committee meetings about the meat pack.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href={`tel:${SUPPORT_PHONE_TEL}`}
              data-testid="golf-hero-cta-call"
              className="inline-flex items-center gap-2 bg-[#D96C32] hover:bg-[#B85A28] text-white font-bold px-6 py-3.5 rounded-md shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <Phone className="w-5 h-5" /> Call {SUPPORT_PHONE}
            </a>
            <a
              href="#register"
              data-testid="golf-hero-cta-register"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur text-white border border-white/30 font-bold px-6 py-3.5 rounded-md transition-colors"
            >
              Request a callback <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href={POSTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="golf-hero-cta-poster"
              className="inline-flex items-center gap-2 text-white/90 hover:text-white font-bold px-2 py-3.5 transition-colors"
            >
              <Download className="w-5 h-5" /> Download the flyer
            </a>
          </div>
        </div>
      </section>

      {/* Who we back — 3 audience pillars */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
        <div className="max-w-2xl">
          <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#D96C32]">
            Who Farm It Forward is for
          </p>
          <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl text-[#1A1D1A] leading-tight">
            Three ways rural communities put the funding to work.
          </h2>
          <p className="mt-4 text-[#4A4F4A] leading-relaxed">
            One programme, one QR code, one automated funding stream — split
            back to whichever rural organisation your members and families
            already back.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
          <Stat
            value="Clubs"
            label="Golf & sports clubs"
            sub="Fairway maintenance, team gear, junior programmes, tournament costs — steady club funding without another rostered sausage sizzle."
            icon={Trophy}
            accent="bg-[#2D4A22]/10 text-[#2D4A22]"
          />
          <Stat
            value="Schools"
            label="Rural schools"
            sub="Uniforms, sports gear, EOTC camps and the little extras that keep small country classrooms punching above their weight."
            icon={GraduationCap}
            accent="bg-[#D96C32]/10 text-[#D96C32]"
          />
          <Stat
            value="Groups"
            label="Community groups"
            sub="Halls, fire brigades, playgrounds, dog trials, A&P shows — the volunteer-run things that hold rural communities together."
            icon={HeartHandshake}
            accent="bg-[#1F5BC1]/10 text-[#1F5BC1]"
          />
        </div>

        <div className="mt-10 bg-white border border-[#D5D1C8] rounded-md p-6 sm:p-8 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-8">
            <div className="flex-1">
              <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#2D4A22]">
                Every community is different
              </p>
              <h3 className="mt-2 font-display font-bold text-xl sm:text-2xl text-[#1A1D1A]">
                We tailor the deal for your committee.
              </h3>
              <p className="mt-2 text-sm sm:text-base text-[#4A4F4A] leading-relaxed">
                Rates, signing bonuses and top-donor tiers get set on a call —
                nothing is off the shelf. Give us 15 minutes with your
                committee, board or PTA and we&apos;ll come back with a proposal
                built around your community.
              </p>
            </div>
            <div className="flex flex-col gap-2 md:min-w-[240px]">
              <a
                href={`tel:${SUPPORT_PHONE_TEL}`}
                data-testid="golf-cta-call"
                className="inline-flex items-center justify-center gap-2 bg-[#2D4A22] hover:bg-[#1F3A18] text-white font-bold px-5 py-3 rounded-md transition-colors"
              >
                <Phone className="w-4 h-4" /> Call {SUPPORT_PHONE}
              </a>
              <a
                href="#register"
                data-testid="golf-cta-scroll-register"
                className="inline-flex items-center justify-center gap-2 border border-[#D5D1C8] hover:bg-[#F9F8F6] text-[#1A1D1A] font-bold px-5 py-3 rounded-md transition-colors"
              >
                Request a callback
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white border-y border-[#D5D1C8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
          <div className="max-w-2xl">
            <p className="text-xs font-bold tracking-[0.18em] uppercase text-[#2D4A22]">
              How Farm It Forward works
            </p>
            <h2 className="mt-3 font-display font-bold text-3xl sm:text-4xl text-[#1A1D1A] leading-tight">
              Members farm. Community earns. You never lift a raffle book.
            </h2>
          </div>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
            <Step
              n="1"
              icon={Sparkles}
              title="Put the poster in front of your people"
              body="We give you a printable A3 with your community's branding and a scan-QR code. Bar wall, notice board, prize-giving slide, school newsletter — wherever your community already looks."
            />
            <Step
              n="2"
              icon={Users}
              title="Farmers scan and sign up"
              body="A farmer scans the code, installs Arvo, and every session they run is automatically tagged to your community. No form-filling. No manual tracking."
            />
            <Step
              n="3"
              icon={Trophy}
              title="Your community gets paid, quarterly"
              body="Every three months we tally sign-ups, spend and job earnings, then pay the total straight to your community account. Full report attached so you know exactly where it came from — quarterly cycles keep the amounts meaningful."
            />
          </div>
        </div>
      </section>

      {/* Register + poster download */}
      <section id="register" className="max-w-6xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <RegisterForm />
          </div>

          <div className="lg:col-span-5 space-y-5">
            <div className="bg-white border border-[#D5D1C8] rounded-md p-6 shadow-sm">
              <div className="flex items-center gap-2 text-[#D96C32] mb-2">
                <Download className="w-5 h-5" />
                <span className="text-xs font-bold tracking-[0.18em] uppercase">
                  Print it, pin it, share it
                </span>
              </div>
              <h3 className="font-display font-bold text-xl text-[#1A1D1A]">
                Point-of-sale poster
              </h3>
              <p className="mt-2 text-sm text-[#4A4F4A] leading-relaxed">
                A ready-to-print A3 poster you can put on the bar wall, next to
                the scorecards or on the notice board while you wait for us to
                come back with a branded version for your club.
              </p>
              <a
                href={POSTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="golf-poster-download-side"
                className="mt-5 inline-flex items-center gap-2 bg-[#2D4A22] hover:bg-[#1F3A18] text-white font-bold px-5 py-2.5 rounded-md transition-colors"
              >
                <Download className="w-4 h-4" /> Download PDF
              </a>
            </div>

            <div className="bg-white border border-[#D5D1C8] rounded-md p-6 shadow-sm">
              <div className="flex items-center gap-2 text-[#2D4A22] mb-2">
                <Mail className="w-5 h-5" />
                <span className="text-xs font-bold tracking-[0.18em] uppercase">
                  Talk to us
                </span>
              </div>
              <h3 className="font-display font-bold text-xl text-[#1A1D1A]">
                Prefer to just have a yarn?
              </h3>
              <p className="mt-2 text-sm text-[#4A4F4A] leading-relaxed">
                Send us an email or give us a bell — we&apos;re happy to walk
                your committee through the numbers.
              </p>
              <div className="mt-4 space-y-2 text-sm">
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  data-testid="golf-mailto"
                  className="inline-flex items-center gap-2 text-[#1A1D1A] hover:text-[#2D4A22] font-bold"
                >
                  <Mail className="w-4 h-4 text-[#4A4F4A]" /> {SUPPORT_EMAIL}
                </a>
                <br />
                <a
                  href={`tel:${SUPPORT_PHONE_TEL}`}
                  data-testid="golf-tel"
                  className="inline-flex items-center gap-2 text-[#1A1D1A] hover:text-[#2D4A22] font-bold"
                >
                  <Phone className="w-4 h-4 text-[#4A4F4A]" /> {SUPPORT_PHONE}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-[#2D4A22] text-white py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <span
                role="img"
                aria-label="Arvo"
                className="block h-10 w-10 rounded-full bg-[#F9F8F6] bg-no-repeat bg-center bg-contain"
                style={{ backgroundImage: "url('/screens/logo-cow.png')" }}
              />
              <div className="leading-tight">
                <p className="font-display font-bold text-lg">arvo</p>
                <p className="text-[11px] font-bold tracking-[0.16em] uppercase text-[#F2C76A]">
                  A Measured Livestock product
                </p>
              </div>
            </div>
            <div className="text-center sm:text-right text-sm text-white/85">
              <p>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-white underline-offset-4 hover:underline">
                  {SUPPORT_EMAIL}
                </a>
                {" · "}
                <a href={`tel:${SUPPORT_PHONE_TEL}`} className="hover:text-white underline-offset-4 hover:underline">
                  {SUPPORT_PHONE}
                </a>
              </p>
              <p className="mt-2 text-xs text-white/70">
                <a href="/" className="hover:text-white">← Back to arvo</a>{" "}·{" "}
                <a href="/privacy" className="hover:text-white">Privacy</a>{" "}·{" "}
                <a href="/support" className="hover:text-white">Support</a>
              </p>
            </div>
          </div>
          <p className="mt-8 text-center text-xs text-white/60">
            &copy; {new Date().getFullYear()} Measured Livestock Ltd — Farm It
            Forward helps rural New Zealand keep the lights on at the local
            club, school and hall.
          </p>
        </div>
      </footer>
    </div>
  );
}
